import React, { useState, useEffect } from "react";
import { Layout, Menu, Table, message, Card, Avatar, Row, Col, Empty, Spin } from "antd";
import { supabase } from "../../config/supabaseClient";
import { useAuth } from "../../config/AuthContext";
import { useNavigate } from "react-router-dom";

const { Sider, Content } = Layout;

const StudentDashboard = () => {
  const { session, profile } = useAuth();
  const [results, setResults] = useState([]);
  const [studentName, setStudentName] = useState("");
  const [studentEmail, setStudentEmail] = useState("");
  const [studentPhoto, setStudentPhoto] = useState("");
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const navigate = useNavigate();

  const fetchResults = async () => {
    if (!session?.user) return;
    setLoading(true);
    setHasSearched(true);
    try {
      const { data, error } = await supabase
        .from("results")
        .select("*")
        .eq("student_id", session.user.id);
      if (error) {
        message.error("Error fetching results!");
        return;
      }
      setResults(data);
    } catch (error) {
      message.error("Error fetching results!");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await supabase.auth.signOut();
      message.success("Logged out successfully!");
      navigate("/");
    } catch (error) {
      message.error("Error logging out: " + error.message);
    }
  };

  useEffect(() => {
    if (session?.user) {
      setStudentEmail(session.user.email);
      setStudentName(profile?.name || session.user.email);
      setStudentPhoto("https://www.w3schools.com/w3images/avatar2.png");
      fetchResults();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, profile]);

  const columns = [
    { title: "Student Name", dataIndex: "student_name", key: "student_name" },
    { title: "Subject", dataIndex: "subject", key: "subject" },
    { title: "Marks", dataIndex: "marks", key: "marks" },
    { title: "Grade", dataIndex: "grade", key: "grade" },
  ];

  return (
    <Layout className="min-h-screen bg-background animate-fade-in">
      <Sider
        width={250}
        className={`bg-gradient-to-b from-primary to-foreground text-white ${
          visible ? "block" : "hidden"
        } md:block fixed md:relative z-50 shadow-lg animate-fade-in`}
        breakpoint="md"
        collapsedWidth="0"
        onBreakpoint={(broken) => setVisible(!broken)}
      >
        <div className="p-6 text-center">
          <Avatar
            src={studentPhoto}
            size={80}
            className="mb-4 shadow-md border-2 border-gray-200"
          />
          <h1 className="text-lg font-bold">{studentName}</h1>
          <p className="text-gray-400">{studentEmail}</p>
        </div>
        <Menu
          theme="none"
          mode="inline"
          className="mt-4 rounded-md"
          items={[
            {
              label: (
                <button
                  onClick={handleLogout}
                  className="w-full text-white text-center bg-red-600 hover:bg-red-500 rounded-md py-2 font-medium"
                >
                  Logout
                </button>
              ),
              key: "logout",
            },
          ]}
        />
      </Sider>

      <Layout>
        <Content className="p-6">
          <Card
            title={
              <div className="flex flex-col items-center py-5">
                <span className="text-xl font-bold text-gray-700">Welcome to Your Dashboard ;</span>
                <button
                  onClick={handleLogout}
                  className="md:hidden text-white my-4 bg-red-600 hover:bg-red-500 rounded-md py-1 px-3"
                >
                  Logout
                </button>
              </div>
            }
            className="rounded-lg shadow-lg bg-white"
          >
            <Row gutter={[16, 16]} align="middle">
              <Col xs={24} md={8}>
                <h2 className="text-lg font-semibold text-center text-gray-700">
                  Welcome, {studentName}!
                </h2>
                <p className="text-gray-500 text-center">Email: {studentEmail}</p>
              </Col>
              <Col xs={24} md={16}>
                <div className="flex items-center justify-end space-x-4">
                  <button
                    onClick={fetchResults}
                    className="bg-green-600 text-white font-medium px-4 py-2 rounded-lg hover:bg-green-700 shadow-md"
                  >
                    Refresh Results
                  </button>
                </div>
              </Col>
            </Row>
          </Card>
          {loading ? (
            <div className="text-center mt-10">
              <Spin size="large" />
            </div>
          ) : hasSearched && results.length === 0 ? (
            <div className="text-center mt-10">
              <Empty description="No results found" />
            </div>
          ) : (
            <Table
              columns={columns}
              dataSource={results}
              rowKey="id"
              pagination={false}
              className="mt-6 rounded-lg shadow-lg border"
            />
          )}
        </Content>
      </Layout>
    </Layout>
  );
};

export default StudentDashboard;
